import React from 'react';
import PropTypes from 'prop-types';

import { mainHeader, subHeader, subjectArea } from './Info.less';

import { mapMSNQTotalToCognitiveSymptoms, mapMSNQScoresToCognitiveChallenges } from 'neuro-calculation-commons';

const cognitiveSymptomsLocalizationsFI = {
  mild: 'Kyselyn perusteella koet korkeintaan ajoittaisia / lieviä kognitiivisia oireita',
  moderate: 'Kyselyn perusteella koet vähintään ajoittaisia / lieviä kognitiivisia oireita',
  severe: 'Kyselyn perusteella koet arkea häiritseviä kognitiivisia oireita',
  verySevere: 'Kyselyn perusteella koet huomattavia kognitiivisia oireita'
};

const cognitiveChallengesLocalizationsFI = {
  concentration: 'keskittymisessä',
  memoryLearning: 'uuden oppimisessa ja/tai muistamisessa',
  problemSolving: 'ongelman ratkaisussa ja/tai kielellisessä suoriutumisessa ja/tai käyttäytymisen säätelyssä'
};

const InfoFI = ({ totalScore, statusMood, statusFatigue, document }) => (
  <React.Fragment>
    <div className={mainHeader}>Yhteenveto kokemastasi kognitiivisesta toimintakyvystä</div>

    <div className={subjectArea}>
      <div className={subHeader}>Kokonaispistemäärä</div>
      <ul>
        <li>
          Sait kyselystä <span style={{ fontWeight: 600 }}>{totalScore}</span> pistettä (pienin mahdollinen pistemäärä
          0, suurin mahdollinen 60; suurempi pistemäärä viitta huomattavampiin kognitiivisiin oireisiin).
        </li>
      </ul>
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>Kokemasi kognitiiviset oireet</div>
      <ul>
        <li>{cognitiveSymptomsLocalizationsFI[mapMSNQTotalToCognitiveSymptoms(totalScore)]}</li>
      </ul>
    </div>

    {mapMSNQScoresToCognitiveChallenges(document.content).length > 0 && (
      <div>
        <ul>
          <li>Kyselyn perusteella koet haasteita erityisesti:</li>
          <ul>
            {mapMSNQScoresToCognitiveChallenges(document.content).map(challenge => (
              <li>{cognitiveChallengesLocalizationsFI[challenge]}</li>
            ))}
          </ul>
        </ul>
      </div>
    )}

    {(statusFatigue > 0 || statusMood > 0) && (
      <div className={subjectArea}>
        <span>
          On hyvä huomata, että kokemasi kognitiiviset oireet saattavat ainakin osittain selittyä kokemallasi
          {statusFatigue > 0 && ' uupumuksella'} {statusFatigue > 0 && statusMood > 0 && ' ja'}{' '}
          {statusMood > 0 && ' mielialan alavireisyydellä'}. Otathan mahdollisen uupumuksen ja mielialan alavireisyyden
          puheeksi MS-hoitajan tai lääkärisi kanssa.
        </span>
      </div>
    )}

    <div className={subjectArea}>
      <div className={subHeader}>Tietoa kognitiivisista oireista</div>
      Tyypillisimpiä MS-tautiin liittyviä kognitiivisia oireita ovat uuden oppimisen työläys, tiedonkäsittelyn
      hidastuminen, keskittymisen häiriöalttius ja entistä herkempi ajatustoimintojen kuormittuminen. Asioiden
      hoitamiseen pitää varata aiempaa enemmän aikaa ja huomioida etukäteen, ettei toiminta aina toteudukaan ennalta
      suunnitellulla tavalla. Toisinaan voi esiintyä myös sanahaun hankaluutta – varsinkin väsyneenä tututkin sanat
      voivat yllättäen olla kateissa. Joissakin tapauksissa myös ympäristön hahmottaminen, esim. tutuissa paikoissa
      liikkuminen, voi vaikeutua. Erityisesti kuormittuessa oman toiminnan säätely voi vaikeutua eikä ajattelu ole
      tällöin yhtä joustavaa kuin pirteänä. MS-tautiin liittyy harvoin kaikkia edellä mainittuja oireita yhtä aikaa.
      <br />
      <br />
      Lievien oireiden kanssa tulee tavallisesti hyvin toimeen kiinnittämällä huomiota omiin toimintatapoihin ja
      käyttämällä erilaisia tukikeinoja, joita useimmat ottavat käyttöön huomaamattaan. Seuraavista vinkeistä voi olla
      sinulle apua:
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>Apua arkeen</div>
      Keskittyminen
      <br />
      <ul>
        <li>jos keskittymisesi on häiriöaltis, pyri karsimaan ympärillä olevat häiriötekijät</li>
        <ul>
          <li>pyri tekemään tärkeät tehtävät mahdollisimman häiriöttömässä tilassa</li>
          <li>käytä tarvittaessa kuulokkeita, sulje radio / TV / puhelin</li>
          <li>vältä kiirettä ja viime hetkessä toimimista</li>
        </ul>
        <li>
          jos useampaan asiaan keskittyminen samanaikaisesti on vaikeaa, pyri tekemään yksi asia loppuun ennen
          seuraavaan siirtymistä
        </li>
        <ul>
          <li>vältä tilanteita, joissa sinulla on monta tehtävää kesken</li>
          <li>kirjaa itsellesi tehtävälista</li>
          <li>varaa itsellesi riittävästi aikaa</li>
        </ul>
        <li>
          jos keskittymisen ylläpitäminen pidemmän aikaa tuottaa hankaluuksia ja/tai väsyt herkästi, tauota tekemistä
        </li>
        <ul>
          <li>
            pidä sopivin väliajoin lyhyitä taukoja: kuuntele hetki musiikkia, sulje silmäsi, tee lyhyt taukojumppa tms.
          </li>
          <li>pyri tekemään tärkeimmät ja kuormittavimmat tehtävät, kun koet olevasi virkeimmilläsi</li>
          <li>vältä pitkiä yhtäjaksoisia työrupeamia, kokouksia tms. ja ehdota taukoja</li>
        </ul>
      </ul>
      <br />
      Uuden oppiminen, muistaminen
      <ul>
        <li>jos oppiminen on aiempaa työläämpää, varaa sille aiempaa enemmän aikaa</li>
        <ul>
          <li>keskity olennaiseen, ole järjestelmällinen, rauhoita oppimisympäristö</li>
          <li>anna oppimiselle riittävästi aikaa, tauota</li>
          <li>jäsennä opittavaa pienemmiksi kokonaisuuksiksi</li>
          <li>pyri toistamaan oppimaasi heti asian opittuasi</li>
          <li>
            tunnista, miten opit parhaiten ja käytä sinulle sopivia oppimisen tukikeinoja: kuvia, sanalistoja,
            opetusvideoita jne.
          </li>
        </ul>
        <li>jos unohdat aiempaa herkemmin, käytä muistin tukikeinoja</li>
        <ul>
          <li>
            vain mielikuvitus on rajana - muistivihot, kalenterit, muistilaput, tehtävälistat, hälytykset jne. ovat
            erittäin käyttökelpoisia, kunhan käytät niitä systemaattisesti
          </li>
          <li>sovi läheistesi kanssa systemaattisesta muistin tukikeinojen, mm. kalenterin käytöstä</li>
          <li>pyri sopimaan myös opiskelussa, työpaikalla ja harrastuksissa muistamista tukevien keinojen käytöstä</li>
        </ul>
      </ul>
      <br />
      Kognitiivinen joustavuus, kielellinen suoriutuminen, käyttäytymisen säätely jne.
      <ul>
        <li>
          jos uudet, tavallisesta poikkeavat tilanteet ovat sinulle haasteellisia, pyri ottamaan aikaa asian
          ratkaisemiseen
        </li>
        <ul>
          <li>pysähdy hetkeksi ja puntaroi ratkaisuvaihtoehtoja – pieni tauko auttaa ajattelemaan selkeämmin</li>
          <li>pilko hoidettava asia pienempiin kokonaisuuksiin ja tee yksi asia kerrallaan</li>
        </ul>
        <li>jos sanojen löytäminen tuottaa hankaluuksia, pyri rauhoittamaan puhetahtia ja anna aikaa itsellesi</li>
        <ul>
          <li>voit kertoa läheisillesi, toivotko aikaa viestisi kertomiseen tai apua sanojen etsinnässä</li>
        </ul>
        <li>jos koet käyttäytyväsi itsellesi poikkeavalla tavalla, ota asia puheeksi läheistesi kanssa</li>
        <ul>
          <li>
            miettikää yhdessä, mistä muutos voi johtua – sairauden aiheuttama epävarmuus, uupumus ja alavireisyys
            saattavat vaikuttaa toimintatapoihin ja käyttäytymiseen
          </li>
          <li>pyri tunnistamaan ja välttämään tunnereaktioita laukaisevia tilanteita tai tekijöitä</li>
        </ul>
        <li>häiriötekijät, kuormittuminen ja uupumus saattavat kiristää pinnaa</li>
        <ul>
          <li>pyri karsimaan häiriötekijöitä</li>
          <li>tarkkaile jaksamistasi, tauota ja lepää</li>
          <li>
            pyri sopimaan läheistesi kanssa toimintatavoista, jotka tukevat jaksamistasi (katso MyMS:n uupumus-osio)
          </li>
        </ul>
      </ul>
    </div>
    <div className={subjectArea}>
      <div className={subHeader}>Miten tästä eteenpäin</div>
      On tärkeää, että pysyt aktiivisena kognitiivisista oireistasi huolimatta. Mielekäs tekeminen ja aivotyö tukevat
      kognitiivisen toimintakyvyn säilymistä hyvänä. Jos kognitiiviset oireesi aiheuttavat haasteita arjessa
      selviytymiseen, esimerkiksi opiskeluissa etenemiseen tai työssä selviytymiseen, ota oireet puheeksi MS-hoitajan
      ja/tai lääkärisi kanssa. Yhdessä voitte arvioida jatkotoimenpiteiden, esimerkiksi neuropsykologisen tutkimuksen
      tarvetta. Kognitiivista toimintakykyä on mahdollista selvitellä tarkemmin ja saatat hyötyä ohjauksesta ja
      kuntoutuksesta. Löydät lisätietoa kuntoutusmahdollisuuksista esimerkiksi Kelan sivuilta{' '}
      <a href="https://www.kela.fi" target="blank">
        www.kela.fi
      </a>{' '}
      (MS-kuntoutuskurssit, harkinnanvarainen ja vaativa yksilöllinen moniammatillinen kuntoutus neurologisella
      linjalla, neuropsykologinen kuntoutus) ja Neuroliitto ry:n sivuilta{' '}
      <a href="https://www.kuntoutukseen.fi" target="blank">
        www.kuntoutukseen.fi
      </a>
      . Oireista on hyvä kertoa myös läheisillesi. Voit käyttää apuna tätä kyselystä saamaasi yhteenvetoa sekä
      Neuroliitto ry:n sivuilta löytyviä oppaita (
      <a href="https://neuroliitto.fi/tuote/kognitio-opas/" target="blank">
        Kognitio-opas
      </a>
      {', '}
      <a href="https://neuroliitto.fi/tuote/kognitiiviset-oireet-tyoelamassa/" target="blank">
        Kognitiiviset oireet työelämässä
      </a>
      ).
    </div>
  </React.Fragment>
);

InfoFI.propTypes = {
  totalScore: PropTypes.number,
  statusMood: PropTypes.number,
  statusFatigue: PropTypes.number,
  document: PropTypes.object
};

export default InfoFI;
